import React, { useState, useEffect, useMemo } from "react";
import Papa from "papaparse";
import { ShimmerThumbnail } from "react-shimmer-effects";

import TOPBAR from "../../components/TopBar";
import SEARCHBAR from "../../components/SeachBar";
import MENUBAR from "../../components/Menu";
import FOOTER from "../../components/Footer";
import { Helmet } from "react-helmet";



export default function WheelTorqueChart() {

    const [isHidden, setIsHidden] = useState(false);
    const [showMenuSide, setShowMenuSide] = useState("");
    const [cartItems, setCartItems] = useState("");
    const [tableData, setTableData] = useState([]);

    const [loading, setLoading] = useState(true);

    /*
     * Get cart items and load CSV
     */
    useEffect(() => {

        const storedCartItems =
            localStorage.getItem("totalCartItems");

        if (storedCartItems) {
            try {
                const parsedCartItems =
                    JSON.parse(storedCartItems);

                const numCartItems =
                    Object.values(parsedCartItems);

                const sum = numCartItems.reduce(
                    (a, b) => Number(a) + Number(b),
                    0
                );

                setCartItems(sum);

            } catch (error) {
                console.error(
                    "Error parsing cart items:",
                    error
                );
            }
        }

        const timer = setTimeout(() => {
            setIsHidden(true);
        }, 3000);

        loadCSV();

        return () => clearTimeout(timer);

    }, []);

    /*
     * Menu sidebar
     */
    const setValueMenuSideBar = (value) => {
        setShowMenuSide(value);
    };

    /*
     * Load Wheel Torque CSV
     */
    const loadCSV = () => {

        setLoading(true);

        fetch("/data/wheel-torque-chart.csv")
            .then((response) => {

                if (!response.ok) {
                    throw new Error(
                        "Failed to load wheel torque CSV"
                    );
                }

                return response.text();
            })
            .then((csvText) => {

                Papa.parse(csvText, {
                    header: true,
                    skipEmptyLines: true,

                    complete: function (results) {

                        setTableData(
                            results.data || []
                        );

                        setLoading(false);
                    },

                    error: function (error) {

                        console.error(
                            "Error parsing wheel torque CSV:",
                            error
                        );

                        setLoading(false);
                    }
                });

            })
            .catch((error) => {

                console.error(
                    "Error loading wheel torque chart:",
                    error
                );

                setLoading(false);
            });
    };

    /*
     * Current URL
     */
    const currentLink = window.location.href;
    const url = new URL(currentLink);
    const updatedUrl = url.toString();

    const groupedData = useMemo(() => {

        const makeGroups = {};

        tableData.forEach((row) => {

            const make = String(
                row.Make || ""
            ).trim();

            const model = String(
                row.Model || ""
            ).trim();

            const trim = String(
                row.Trim || ""
            ).trim();

            const torque = String(
                row.Torque || ""
            ).trim();

            const year = parseInt(
                row.Year,
                10
            );

            /*
             * Skip rows without Make
             */
            if (!make) {
                return;
            }

            /*
             * Create Make group
             */
            if (!makeGroups[make]) {
                makeGroups[make] = {};
            }

            /*
             * Create unique combination
             *
             * Model + Trim + Torque
             */
            const uniqueKey = [
                model.toLowerCase(),
                trim.toLowerCase(),
                torque.toLowerCase()
            ].join("|");

            if (!makeGroups[make][uniqueKey]) {

                makeGroups[make][uniqueKey] = {
                    Make: make,
                    Model: model,
                    Trim: trim,
                    Torque: torque,
                    years: []
                };
            }

            /*
             * Add valid year
             */
            if (!isNaN(year)) {
                makeGroups[make][uniqueKey].years.push(
                    year
                );
            }
        });

        /*
         * Convert grouped object into
         * final display structure
         */
        const finalGroups = {};

        Object.keys(makeGroups).forEach((make) => {

            finalGroups[make] = Object.values(
                makeGroups[make]
            ).map((item) => {

                const years = [
                    ...new Set(item.years)
                ].sort((a, b) => a - b);

                let yearRange = "-";

                if (years.length === 1) {

                    yearRange = `${years[0]}`;

                } else if (years.length > 1) {

                    const minYear = Math.min(
                        ...years
                    );

                    const maxYear = Math.max(
                        ...years
                    );

                    yearRange =
                        `${minYear} - ${maxYear}`;
                }

                return {
                    Make: item.Make,
                    Model: item.Model,
                    Trim: item.Trim,
                    Year: yearRange,
                    Torque: item.Torque
                };
            });
        });

        return finalGroups;

    }, [tableData]);

    return (
        <>
            <Helmet>

                <title>
                    Wheel Torque Chart
                </title>

                <meta
                    name="description"
                    content="Wheel torque chart for all vehicles with recommended torque specifications."
                />

                <link
                    rel="canonical"
                    href={updatedUrl}
                />

            </Helmet>

            <TOPBAR
                isHidden={isHidden}
            />

            <SEARCHBAR
                setValueMenuSideBar={
                    setValueMenuSideBar
                }
                cartItemsdata={
                    cartItems
                }
            />

            <MENUBAR
                showMenuSide={
                    showMenuSide
                }
                setValueMenuSideBar={
                    setValueMenuSideBar
                }
            />

            <div className="parformane-bar"
                style={{ background: "#494949" }}
            >
                <div className="container d-flex justify-content-between">
                    <ul className="p-0 m-0 listing-parmofamne">
                        <li><a href="/">Home /</a></li>
                        <li className='active-bread text-decoration-none'><a href="#">Wheel Torque Chart</a></li>
                    </ul>

                </div>
            </div>

            <section className="tech-center-page">

                <div className="container">

                    <h1 className="text-center heading">
                        Wheel Torque Chart
                    </h1>

                    <p className="text-center mb-5">
                        If you are planning on installing a wheel
                        on your vehicle, it is important to be aware
                        of the manufacturer recommended torque settings.
                        Using the proper torque helps ensure that the
                        wheel assembly is securely installed. Using the
                        manufacturer recommended torque also helps
                        guarantee that there isn’t either too much or
                        too little pressure on any of the assemblies
                        to which the wheel attaches. Before you install
                        your wheels, check your vehicle’s recommended
                        torque.
                    </p>

                    <div className="table-responsive">

                        <table className="table table-bordered torque-table">

                            <tbody>

                                {/* ================================
                                    SHIMMER LOADING
                                ================================= */}

                                {loading ? (

                                    Array.from({
                                        length: 5
                                    }).map((_, index) => (

                                        <React.Fragment
                                            key={index}
                                        >

                                            {/* Header */}
                                            <tr className="repeat-header">

                                                {Array.from({
                                                    length: 5
                                                }).map(
                                                    (_, headerIndex) => (

                                                        <th
                                                            key={
                                                                headerIndex
                                                            }
                                                        >

                                                            <ShimmerThumbnail
                                                                height={20}
                                                                rounded
                                                                className="mb-0"
                                                            />

                                                        </th>

                                                    )
                                                )}

                                            </tr>

                                            {/* Data row */}
                                            <tr>

                                                {Array.from({
                                                    length: 5
                                                }).map(
                                                    (_, cellIndex) => (

                                                        <td
                                                            key={
                                                                cellIndex
                                                            }
                                                        >

                                                            <ShimmerThumbnail
                                                                height={20}
                                                                rounded
                                                                className="mb-0"
                                                            />

                                                        </td>

                                                    )
                                                )}

                                            </tr>

                                        </React.Fragment>
                                    ))

                                ) : Object.keys(groupedData).length > 0 ? (

                                    /*
                                     * ================================
                                     * ACTUAL DATA
                                     * ================================
                                     */

                                    Object.keys(
                                        groupedData
                                    ).map(
                                        (make, index) => (

                                            <React.Fragment
                                                key={index}
                                            >

                                                {/* Table Header */}
                                                <tr className="repeat-header">
                                                    <th> Make </th>
                                                    <th> Model</th>
                                                    <th> Trim</th>
                                                    <th> Year Range </th>
                                                    <th> Torque</th>
                                                </tr>

                                                {/* Combined Rows */}
                                                {groupedData[
                                                    make
                                                ].map(
                                                    (row, i) => (
                                                        <tr key={`${make}-${row.Model}-${row.Trim}-${row.Torque}-${i}`}>
                                                            <td>{row.Make}</td>
                                                            <td>{row.Model}</td>
                                                            <td>{row.Trim}</td>
                                                            <td>{row.Year}</td>
                                                            <td>{row.Torque || "-"}</td>
                                                        </tr>
                                                    )
                                                )}

                                            </React.Fragment>

                                        )
                                    )

                                ) : (

                                    /*
                                     * ================================
                                     * NO DATA
                                     * ================================
                                     */

                                    <tr>

                                        <td
                                            colSpan="5"
                                            className="text-center py-4"
                                        >
                                            No wheel torque chart data found.
                                        </td>

                                    </tr>

                                )}

                            </tbody>

                        </table>

                    </div>

                </div>

            </section>

            <FOOTER />

        </>
    );
}



















// import React, { useState, useEffect } from "react";
// import Papa from "papaparse";

// import TOPBAR from "../../components/TopBar";
// import SEARCHBAR from "../../components/SeachBar";
// import MENUBAR from "../../components/Menu";
// import FOOTER from "../../components/Footer";
// import { Helmet } from "react-helmet";

// export default function WheelTorqueChart() {

//     const [isHidden, setIsHidden] = useState(false);
//     const [showMenuSide, setShowMenuSide] = useState("");
//     const [cartItems, setCartItems] = useState("");
//     const [tableData, setTableData] = useState([]);


//     useEffect(() => {

//         const storedCartItems = localStorage.getItem("totalCartItems");

//         if (storedCartItems) {
//             const parsedCartItems = JSON.parse(storedCartItems);
//             const numCartItems = Object.values(parsedCartItems);
//             const sum = numCartItems.reduce((a, b) => a + b, 0);
//             setCartItems(sum);
//         }

//         setTimeout(() => {
//             setIsHidden(true);
//         }, 3000);

//         loadCSV();

//     }, []);

//     const setValueMenuSideBar = (value) => {
//         setShowMenuSide(value);
//     };

//     const loadCSV = () => {

//         fetch("/data/wheel-torque-chart.csv")
//             .then((response) => response.text())
//             .then((csvText) => {

//                 Papa.parse(csvText, {
//                     header: true,
//                     skipEmptyLines: true,
//                     complete: function (results) {
//                         setTableData(results.data);
//                     }
//                 });

//             });

//     };

//     const currentLink = window.location.href;
//     const url = new URL(currentLink);
//     const updatedUrl = url.toString();

//     const groupedData = tableData.reduce((acc, row) => {
//         const make = row.Make;

//         if (!acc[make]) {
//             acc[make] = [];
//         }

//         acc[make].push(row);

//         return acc;
//     }, {});

//     return (
//         <>
//             <Helmet>
//                 <title>Wheel Torque Chart</title>
//                 <meta
//                     name="description"
//                     content="Wheel torque chart for all vehicles with recommended torque specifications."
//                 />
//                 <link rel="canonical" href={updatedUrl} />
//             </Helmet>

//             <TOPBAR isHidden={isHidden} />
//             <SEARCHBAR setValueMenuSideBar={setValueMenuSideBar} cartItemsdata={cartItems} />
//             <MENUBAR showMenuSide={showMenuSide} setValueMenuSideBar={setValueMenuSideBar} />

//             <section className="tech-center-page">
//                 <div className="container">

//                     <h1 className="text-center heading">Wheel Torque Chart</h1>

//                     <p className="text-center mb-5">
//                         If you are planning on installing a wheel on your vehicle, it is important to be aware of the manufacturer recommended torque settings. Using the proper torque helps ensure that the wheel assembly is securely installed. Using the manufacturer recommended torque also helps guarantee that there isn’t either too much or too little pressure on any of the assemblies to which the wheel attaches. Before you install your wheels, check your vehicle’s recommended torque.
//                     </p>

//                     <div className="table-responsive">

//                         <table className="table table-bordered torque-table">

//                             <tbody>

//                                 {Object.keys(groupedData).map((make, index) => (
//                                     <React.Fragment key={index}>

//                                         <tr className="repeat-header">
//                                             <th>Make</th>
//                                             <th>Model</th>
//                                             <th>Trim</th>
//                                             <th>Year Range</th>
//                                             <th>Torque</th>
//                                         </tr>

//                                         {groupedData[make].map((row, i) => (
//                                             <tr key={i}>
//                                                 <td>{i === 0 ? make : ""}</td>
//                                                 <td>{row.Model}</td>
//                                                 <td>{row.Trim}</td>
//                                                 <td>{row.Year}</td>
//                                                 <td>{row.Torque}</td>
//                                             </tr>
//                                         ))}

//                                     </React.Fragment>
//                                 ))}

//                             </tbody>

//                         </table>

//                     </div>

//                 </div>
//             </section>

//             <FOOTER />
//         </>
//     );
// }